import { fg } from '@atlaskit/platform-feature-flags';
import {
	buildImageUrl,
	isImageCreateData,
	PAGE_CREATE_ACTIONS,
	WHITEBOARD_CREATE_ACTIONS,
	DATABASE_CREATE_ACTIONS,
	SLIDES_CREATE_ACTIONS,
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	type ConfluenceAIFCWhiteboardCreateActionPayload,
	isConfluenceAIFCWhiteboardCreateActionData,
	AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
	LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY,
	DYNAMIC_UI_CREATE_KEY,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import {
	isStreamActionClassification,
	type StreamActionClassification,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { fetchMauiPersonalReadToken } from '@atlassian/conversation-assistant-service/common/utils/maui-media-token';
import type {
	StoreAssistant,
	StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { isStaging } from '@atlassian/conversation-assistant-viz/utils/env-helpers';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { getConfluenceWhiteboardClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-whiteboard-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { EditorInsertContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import {
	findLastMessageWithAIFCAction,
	getAIFCActionStatus,
} from '@atlassian/smart-creation/utils';

import type { ContentClassification } from './content-classification';

export const getFinalAIFCMessage = (chatMessages: StoreChatItem[]): StoreAssistant | undefined => {
	const latestAIFCActionMessage = findLastMessageWithAIFCAction(chatMessages);

	return getAIFCActionStatus(latestAIFCActionMessage) === 'complete' &&
		latestAIFCActionMessage?.content?.streamingComplete
		? latestAIFCActionMessage
		: undefined;
};

/**
 * Semantic wrapper around the shared stream guard. Keeping this local name makes the
 * early-insert flow read in product terms while still relying on the canonical type guard.
 */
export const isActionClassificationMessage = (
	message: unknown,
): message is StreamActionClassification => isStreamActionClassification(message);

/**
 * Returns true only when the backend has explicitly classified the stream as a
 * Confluence whiteboard create. Early diagram insertion must be based on this
 * classification, not on partial action payload shape, to avoid false positives.
 */
export const isWhiteboardCreateActionClassification = (
	message: unknown,
): message is StreamActionClassification =>
	isActionClassificationMessage(message) &&
	message.message.action_key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY;

export const getContentClassificationFromActionClassification = (
	message: unknown,
): ContentClassification | undefined => {
	if (isWhiteboardCreateActionClassification(message)) {
		return {
			type: 'whiteboard',
			actionClassification: message,
		};
	}
	return undefined;
};

export const withWhiteboardCreateActionData = (
	classification: ContentClassification | undefined,
	data: unknown,
): ContentClassification | undefined => {
	if (
		classification?.type === 'whiteboard' &&
		isConfluenceAIFCWhiteboardCreateActionData(data)
	) {
		return { ...classification, data };
	}
	return classification;
};

export const getWhiteboardCreateActionData = (
	message: StoreAssistant | undefined,
): ConfluenceAIFCWhiteboardCreateActionPayload | undefined => {
	const action = message?.content?.message?.message?.actions?.[0];
	if (
		action?.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY &&
		isConfluenceAIFCWhiteboardCreateActionData(action.data)
	) {
		return action.data;
	}
	return undefined;
};

/**
 * Different Remix types use different property names:
 * - MaUI Apps (DYNAMIC_UI_CREATE_KEY) use 'id'
 * - Diagrams are whiteboards, so use 'contentId'
 */
export const getRemixIdFromMessage = ({
	message,
	actionKey: actionKeyOverride,
}: {
	message: StoreAssistant | undefined;
	actionKey?: string;
}): string | undefined => {
	const actionKey = actionKeyOverride || message?.content?.message?.message?.actions?.[0]?.key;
	const actionData = message?.content?.message?.message?.actions?.[0]?.data as
		| { contentId?: string; id?: string }
		| undefined;

	switch (actionKey) {
		case DYNAMIC_UI_CREATE_KEY:
			return actionData?.id;
		case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
		case AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
		case LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
			return actionData?.contentId;
		default:
			return undefined;
	}
};

/**
 * Constructs a MaUI App URL from appId.
 * Uses the format: {origin}/remix/ui/{appId}
 */
export const getMauiUrl = (appId: string): string => {
	return `${window.location.origin}/remix/ui/${encodeURIComponent(appId)}`;
};

/**
 * Constructs a whiteboard URL using `_` as a placeholder space key.
 * The `isRemixDiagram=true` param signals to the paste plugin that this
 * whiteboard may need to be moved to the current page's space.
 */
const getWhiteboardUrl = (contentId: string, spaceId?: string): string => {
	const spaceSegment = spaceId ? `~${spaceId}` : '~';
	return `${window.location.origin}/wiki/spaces/${spaceSegment}/whiteboard/${contentId}?isRemixDiagram=true`;
};

/**
 * Builds the temporary embedded whiteboard URL used while the whiteboard is still
 * streaming. The `rovoReceiverId` query param is intentionally product-level
 * terminology; native embed infrastructure maps it to the iframe's
 * `data-rovo-receiver-id` attribute so streaming create/update bridge commands
 * can target the content-specific whiteboard client immediately.
 */
export const getStreamingWhiteboardEmbedUrl = (contentId: string): string => {
	const url = new URL(
		`/wiki/spaces/_/whiteboard/${encodeURIComponent(contentId)}`,
		window.location.origin,
	);
	url.searchParams.set('parentProduct', 'rovo');
	url.searchParams.set('embed', 'true');
	url.searchParams.set('hideBorder', 'true');
	url.searchParams.set('rovoReceiverId', getConfluenceWhiteboardClientId(contentId));
	return url.toString();
};

/**
 * Determines the appropriate content URL based on the action type.
 * For MaUI App actions, returns a MaUI URL.
 * For Diagram actions, returns a whiteboard URL.
 * Exported for testing purposes.
 */
export const getRemixUrl = (
	remixId: string | undefined,
	actionKey: string | undefined,
	spaceId?: string,
): string | undefined => {
	if (!remixId) {
		return undefined;
	}

	switch (actionKey) {
		case DYNAMIC_UI_CREATE_KEY:
			return getMauiUrl(remixId);
		case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
		case AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
		case LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
			return getWhiteboardUrl(remixId, spaceId);
		default:
			return undefined;
	}
};

const buildEmbedCardContent = (url: string) => {
	return [
		{
			type: 'embedCard',
			attrs: {
				url,
				layout: 'center',
			},
		},
	];
};

/**
 * Inserts the content URL into the current page's editor as a sized embed card.
 * Uses `insertReceiverId` to route to the correct editor instance:
 * - For Confluence: `context.confluence.content.id` (the Confluence page ID)
 * - For other products (e.g. Jira, gated by platform_editor_maui_jira_updates):
 *   `context.editor.editorId` (the editor UUID registered with the rovo bridge)
 */
export const insertEmbedIntoPage = ({
	url,
	insertReceiverId,
	isUpdate,
}: {
	url: string;
	insertReceiverId: string | undefined;
	isUpdate?: boolean;
}): void => {
	if (!insertReceiverId) {
		return;
	}

	getBrowserBridgeAPI().send(EditorInsertContentCommand, {
		senderId: ROVO_CHAT_CLIENT_ID,
		receiverId: getConfluenceEditorClientId(insertReceiverId),
		mode: 'insert',
		placement:
			isUpdate && fg('platform_editor_ai_remix_toolbar') ? 'replace-selection' : 'after-block',
		useStoredSelection: true,
		data: {
			type: 'text/adf',
			content: buildEmbedCardContent(url),
		},
	});
};

/**
 * Returns true when the remix was triggered at the page/object level
 * (i.e. from the sidebar "Remix page" button) rather than from a
 * content selection.  Page-level remix sets empty localId strings.
 */
export const isPageLevelRemix = (source: unknown): boolean => {
	if (!source || typeof source !== 'object') {
		return false;
	}
	const s = source as Record<string, unknown>;
	return s.startLocalId === '' && s.endLocalId === '';
};

/**
 * Determines whether the given action requires reparenting content in Confluence.
 * - MaUI Apps: No reparenting needed (they only exist as embeds)
 * - Diagrams: Reparenting needed (Whiteboards live in Confluence as standalone objects)
 * Exported for testing purposes.
 */
export const needsReparenting = (actionKey: string | undefined): boolean => {
	switch (actionKey) {
		case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
		case AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
		case LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
			return true;
		case DYNAMIC_UI_CREATE_KEY:
		default:
			return false;
	}
};
/**
 * Determines the content type for reparenting operations.
 * Only relevant if needsReparenting returns true.
 * Exported for testing purposes.
 */
export const getReparentContentType = (
	actionKey: string | undefined,
): 'whiteboard' | 'database' | undefined => {
	switch (actionKey) {
		case CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY:
		case AIFC_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
		case LEGACY_CONFLUENCE_WHITEBOARD_DIAGRAMMING_KEY:
			return 'whiteboard';
		default:
			return undefined;
	}
};

/**
 * Action keys for content types that should call resolveConversationAction
 * on the convo-ai endpoint to finalize. These are Confluence objects
 * (pages, whiteboards, databases) that need BE finalization.
 * MAUI apps (DYNAMIC_UI_CREATE_KEY) and images are excluded.
 */
export const FINALIZABLE_CONTENT_ACTION_KEYS: Set<string> = new Set([
	...PAGE_CREATE_ACTIONS,
	...WHITEBOARD_CREATE_ACTIONS,
	...DATABASE_CREATE_ACTIONS,
	...SLIDES_CREATE_ACTIONS,
]);

export async function fetchInfographicUrl({
	mediaFileId,
	clientId,
	product,
}: {
	mediaFileId: string;
	clientId: string;
	product: string;
}): Promise<string> {
	const { token, collection } = await fetchMauiPersonalReadToken(product);
	const imageUrl = buildImageUrl({
		fileId: mediaFileId,
		token: token.split('Bearer ')[1], // TODO: Consider moving into fetchMauiPersonalReadToken
		collection,
		clientId,
		isStaging: isStaging(),
	});

	return imageUrl;
}

export const getMediaIdFromMessage = (message: StoreAssistant | undefined): string | undefined => {
	const actionData = message?.content?.message?.message?.actions?.[0]?.data as
		| { contentId?: string; id?: string }
		| undefined;

	return isImageCreateData(actionData) ? actionData.mediaFileId : undefined;
};
